import WebGLObject from './WebGLObject';
import TexturesController from './TexturesController';
import { PLANE_GEOMETRY } from '../../../3D/constants';
import { FrontSide, ShaderMaterial, Vector2 } from 'three';
import CanvasColorTexture from './CanvasColorTexture';
import { RESPONSIVE_IMAGE_FRAGMENT, RESPONSIVE_IMAGE_VERTEX } from './shaders/responsive-image';

export default class Image extends WebGLObject {
    texture;

    set hover(__h) {
        this.material.uniforms.hover.value = __h ? 1 : 0;
    }

    constructor(opts = {}) {
        super(opts);

        if (!this.geometry) {
            this.geometry = PLANE_GEOMETRY;
        }

        if (!this.material) {
            this.setupMaterial();
        }

        this.init();
    }

    init() {
        super.init();
        this.loadTexture();
        this.resize();
    }

    setupMaterial() {
        this.material = new ShaderMaterial({
            uniforms: {
                texture1: { type: 't', value: new CanvasColorTexture('#000000').texture },
                texture2: { type: 't', value: new CanvasColorTexture('#000000').texture },
                mask: { type: 't', value: new CanvasColorTexture().texture },
                textureOverlay: { type: 't', value: new CanvasColorTexture('#000000').texture },
                hover: { type: 'f', value: 0 },
                opacity: { type: 'f', value: 1 },
                imageScale: { type: 'f', value: 1 },
                darken: { type: 'f', value: 0 },
                resolution: {
                    type: 'v2',
                    value: { x: 1, y: 1 }
                },
                mouse: {
                    type: 'v2',
                    value: { x: 0, y: 0 }
                }
            },
            fragmentShader: RESPONSIVE_IMAGE_FRAGMENT,
            vertexShader: RESPONSIVE_IMAGE_VERTEX,
            transparent: true,
            side: FrontSide
        });
    }

    loadTexture() {
        if (this.dom.dataset.src) {
            TexturesController.load({
                src: this.dom.dataset.src,
                material: this.material.uniforms.texture1,
                attribute: "value"
            });
        }

        if (this.dom.dataset.srcHover) {
            TexturesController.load({
                src: this.dom.dataset.srcHover,
                material: this.material.uniforms.texture2,
                attribute: "value"
            });
        }

        if (this.dom.dataset.mask) {
            TexturesController.load({
                src: this.dom.dataset.mask,
                material: this.material.uniforms.texture1,
                attribute: "value"
            });
        }
    }

    update() {
        super.update();

        const { x, y, width, height } = this.dom.getBoundingClientRect();
        const position = this.domPositionTo3D(x, y);
        this.pos.x = position.x + width * .5;
        this.pos.y = position.y - height * .5;
        this.pos.z = parseInt(this.dom.dataset.layer);
    }

    calculateResolution() {
        const width = this.size.x;
        const height = this.size.y;

        const planeAspect = this.mesh.scale.y / this.mesh.scale.x;
        let a1;
        let a2;

        if (height / width > planeAspect) {
            const h = (width / height) * planeAspect;
            // Canvas more horizontal than image
            a1 = 1;
            a2 = h;
        } else {
            // Canvas more vertical than image
            a1 = height / width / planeAspect;
            a2 = 1;
        }

        return {
            x: a1,
            y: a2
        };
    }

    resize() {
        const { x, y, width, height } = this.dom.getBoundingClientRect();
        const position = this.domPositionTo3D(x, y);

        this.position.x = this.pos.x = position.x + width * .5;
        this.position.y = this.pos.y = position.y - height * .5;
        this.pos.z = parseInt(this.dom.dataset.layer);

        super.resize(width, height);

        this.material.uniforms.resolution.value = this.calculateResolution();
    }
}
